import type { JsonValue } from "@protobuf-ts/runtime";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClientConfig } from "@/generated/backend/v1/config";
import {
  FamilyDefaultsResponse,
  type FamilyDocument,
  FamilyDocument as FamilyDocumentType,
  SaveFamilyResponse,
  SaveQuestionnaireRequest,
  SubmitCheckInRequest,
} from "@/generated/backend/v1/family";
import { CreateUserRequest, UpdateUserEmployerRequest } from "@/generated/backend/v1/messages";
import {
  CreatePlanJobRequest,
  CreateScenarioRequest,
  ListScenariosResponse,
  PlanJob,
  PlanJob_State,
  RenameScenarioRequest,
  Scenario,
  ShortfallFixesState,
  StoredPlan,
} from "@/generated/backend/v1/plan";
import { User } from "@/generated/backend/v1/user";
import type { PlanRefreshAnswers, Questionnaire } from "@/generated/core/v1/questionnaire";
import { useAuth } from "@/auth/AuthProvider";
import { fetchClientConfig, readCachedConfig } from "./clientConfig";
import { clearDraft } from "@/plan-logic/questionnaire/draft";
import { listFlinksConnections } from "@/flinks/api";
import { ApiError, fetchApi, fetchOrNull, READ_OPTIONS } from "./fetchApi";

/**
 * All server state goes through TanStack Query (architecture-redesign.md item A). Keys are scoped
 * by the Firebase uid; the auth layer drops everything but `config` when the user changes.
 * Window-focus refetching (the default) replaces the legacy `useAppRefresh`.
 */
export const keys = {
  config: ["config"] as const,
  user: (uid: string) => ["user", uid] as const,
  family: (uid: string) => ["family", uid] as const,
  plan: (uid: string) => ["plan", uid] as const,
  planJob: (id: string) => ["planJob", id] as const,
  defaults: (uid: string, version: number) => ["defaults", uid, version] as const,
  scenarios: (uid: string) => ["scenarios", uid] as const,
  scenario: (id: string) => ["scenario", id] as const,
  lastJob: (uid: string) => ["lastJob", uid] as const,
  bankLink: (uid: string) => ["bankLink", uid] as const,
  billing: (uid: string) => ["billing", uid] as const,
};

/** How often running jobs are polled. */
export const POLL_MS = 2500;

export function isJobActive(job: PlanJob | undefined | null): boolean {
  return !!job && (job.state === PlanJob_State.QUEUED || job.state === PlanJob_State.RUNNING);
}

// ---------------------------------------------------------------- config

export function useClientConfig() {
  return useQuery<ClientConfig>({
    queryKey: keys.config,
    queryFn: fetchClientConfig,
    initialData: readCachedConfig,
    // The cached copy is only a start; always confirm with the backend once per session.
    initialDataUpdatedAt: 0,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------- user

function useUid(): string | null {
  return useAuth().user?.uid ?? null;
}

/** GET /api/user, bootstrapping it with POST /api/user on first sign-in (404). */
export async function getOrCreateUser(): Promise<User> {
  const existing = await fetchOrNull("/user", { response: User });
  if (existing) return existing;
  return fetchApi("/user", {
    body: CreateUserRequest.create({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    request: CreateUserRequest,
    response: User,
  });
}

export function useUser() {
  const uid = useUid();
  return useQuery({
    queryKey: keys.user(uid ?? "-"),
    queryFn: getOrCreateUser,
    enabled: !!uid,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------- family

export function useFamily(enabled = true) {
  const uid = useUid();
  return useQuery({
    queryKey: keys.family(uid ?? "-"),
    queryFn: () => fetchOrNull("/family", { response: FamilyDocumentType }),
    enabled: !!uid && enabled,
    staleTime: 30_000,
  });
}

export function useFamilyDefaults(version: number | undefined) {
  const uid = useUid();
  return useQuery({
    queryKey: keys.defaults(uid ?? "-", version ?? -1),
    queryFn: () => fetchApi("/family/defaults", { response: FamilyDefaultsResponse }),
    enabled: !!uid && version !== undefined,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------- plan

/** The plan job queued by the last save, so "no plan yet" can show progress. */
function rememberJob(client: QueryClient, uid: string, job: PlanJob | undefined) {
  if (!job) return;
  client.setQueryData(keys.lastJob(uid), job);
  client.setQueryData(keys.planJob(job.id), job);
}

export function useLastJob(): PlanJob | undefined {
  const uid = useUid();
  const { data } = useQuery<PlanJob | null>({
    queryKey: keys.lastJob(uid ?? "-"),
    queryFn: () => null,
    enabled: false,
    initialData: null,
  });
  return data ?? undefined;
}

export function usePlan(enabled = true) {
  const uid = useUid();
  const lastJob = useLastJob();
  const client = useQueryClient();
  return useQuery({
    queryKey: keys.plan(uid ?? "-"),
    queryFn: async () => {
      try {
        return await fetchApi("/plan", { response: StoredPlan });
      } catch (e) {
        if (!(e instanceof ApiError && e.isNotFound)) throw e;
        // No plan yet (or an old-format plan being recomputed): the 404 body names the job, the
        // one building the first plan or the latest one when it failed.
        const body = e.body as { pendingJob?: unknown; failedJob?: unknown } | null;
        const job = body?.pendingJob ?? body?.failedJob;
        if (job && uid) {
          rememberJob(client, uid, PlanJob.fromJson(job as JsonValue, READ_OPTIONS));
        }
        return null;
      }
    },
    enabled: !!uid && enabled,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const plan = query.state.data;
      if (plan === undefined) return false;
      if (plan === null) return isJobActive(lastJob) ? POLL_MS : false;
      if (isJobActive(plan.pendingJob)) return POLL_MS;
      if (plan.shortfallFixesState === ShortfallFixesState.PENDING) {
        return POLL_MS * 2;
      }
      return false;
    },
  });
}

/** Polls a plan job until it finishes, then refreshes the plan. */
export function usePlanJob(id: string | undefined) {
  const uid = useUid();
  const client = useQueryClient();
  return useQuery({
    queryKey: keys.planJob(id ?? "-"),
    queryFn: async () => {
      const job = await fetchApi(`/plan-jobs/${encodeURIComponent(id!)}`, { response: PlanJob });
      if (!isJobActive(job) && uid) {
        client.setQueryData(keys.lastJob(uid), job);
        await client.invalidateQueries({ queryKey: keys.plan(uid) });
      }
      return job;
    },
    enabled: !!uid && !!id,
    // A job that isn't this user's (or no longer exists) is a 404: nothing to wait for.
    refetchInterval: (query) =>
      query.state.error instanceof ApiError && query.state.error.isNotFound
        ? false
        : isJobActive(query.state.data) || !query.state.data
          ? POLL_MS
          : false,
  });
}

export function useCreatePlanJob() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi("/plan-jobs", {
        body: CreatePlanJobRequest.create(),
        request: CreatePlanJobRequest,
        response: PlanJob,
      }),
    onSuccess: async (job) => {
      if (!uid) return;
      rememberJob(client, uid, job);
      await client.invalidateQueries({ queryKey: keys.plan(uid) });
    },
  });
}

// ---------------------------------------------------------------- saves

export function onSaved(client: QueryClient, uid: string, res: SaveFamilyResponse) {
  if (res.document) client.setQueryData<FamilyDocument | null>(keys.family(uid), res.document);
  rememberJob(client, uid, res.planJob);
  client.setQueryData<User>(keys.user(uid), (u) => (u ? { ...u, hasFamily: true } : u));
  void client.invalidateQueries({ queryKey: keys.plan(uid) });
  void client.invalidateQueries({ queryKey: ["defaults", uid] });
  // A check-in applies the bank balances that were waiting for it.
  void client.invalidateQueries({ queryKey: keys.bankLink(uid) });
}

/**
 * GET /api/bank-link/connections: the user's Flinks connections and the balances waiting for the next
 * check-in. Only where Flinks is configured.
 */
export function useBankConnections() {
  const uid = useUid();
  const enabled = !!useClientConfig().data?.flinksEnabled;
  return useQuery({
    queryKey: keys.bankLink(uid ?? "-"),
    queryFn: listFlinksConnections,
    enabled: !!uid && enabled,
    staleTime: 5 * 60_000,
  });
}

export type SaveQuestionnaireInput = { questionnaire: Questionnaire; expectedVersion: number };

/** POST /api/family/questionnaire (onboarding, Update Data, Sharpen). 409 and 422 are ApiErrors. */
export function useSaveQuestionnaire() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ questionnaire, expectedVersion }: SaveQuestionnaireInput) =>
      fetchApi("/family/questionnaire", {
        body: SaveQuestionnaireRequest.create({ questionnaire, expectedVersion }),
        request: SaveQuestionnaireRequest,
        response: SaveFamilyResponse,
      }),
    onSuccess: (res) => uid && onSaved(client, uid, res),
  });
}

/** POST /api/family/check-in with the plan-refresh answers. */
export function useCheckIn() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      answers,
      expectedVersion,
    }: {
      answers: PlanRefreshAnswers;
      expectedVersion: number;
    }) =>
      fetchApi("/family/check-in", {
        body: SubmitCheckInRequest.create({ answers, expectedVersion }),
        request: SubmitCheckInRequest,
        response: SaveFamilyResponse,
      }),
    onSuccess: (res) => uid && onSaved(client, uid, res),
  });
}

/** DELETE /api/family: the household, its plans and scenarios. */
export function useDeleteFamily() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi("/family", { method: "DELETE" }),
    onSuccess: () => {
      clearDraft();
      if (!uid) return;
      client.setQueryData(keys.family(uid), null);
      client.setQueryData(keys.plan(uid), null);
      client.setQueryData(keys.lastJob(uid), null);
      client.setQueryData<User>(keys.user(uid), (u) => (u ? { ...u, hasFamily: false } : u));
      void client.invalidateQueries({ queryKey: keys.user(uid) });
    },
  });
}

// ---------------------------------------------------------------- user settings

export function useUpdateEmployer() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { productKey: string; companyName: string }) =>
      fetchApi("/user/employer", {
        method: "PUT",
        body: UpdateUserEmployerRequest.create(input),
        request: UpdateUserEmployerRequest,
        response: User,
      }),
    onSuccess: (user) => uid && client.setQueryData(keys.user(uid), user),
  });
}

/** Stripe checkout (plain JSON, as today): redirects the browser to the session URL. */
export function useCheckout() {
  return useMutation({
    mutationFn: async () => {
      const res = await fetchApi<{ url?: string }>("/stripe/create-checkout-session", { json: {} });
      if (!res?.url) throw new Error("Checkout isn't available right now.");
      window.location.assign(res.url);
      return res.url;
    },
  });
}

/** The user's recurring billing (plain JSON, like checkout); null when they have none. */
export interface Billing {
  /** Cancelled: no more charges, and access ends at `currentPeriodEnd`. */
  cancelAtPeriodEnd: boolean;
  /** End of the paid-for period (ISO 8601). */
  currentPeriodEnd: string;
}

export function useBilling(enabled: boolean) {
  const uid = useUid();
  return useQuery({
    queryKey: keys.billing(uid ?? "-"),
    queryFn: async () =>
      (await fetchApi<{ subscription?: Billing | null }>("/stripe/subscription")).subscription ??
      null,
    enabled: enabled && !!uid,
  });
}

/** Stops recurring billing at the end of the paid-for period; access is unchanged until then. */
export function useCancelBilling() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await fetchApi<{ subscription?: Billing | null }>("/stripe/cancel-subscription", {
          json: {},
        })
      ).subscription ?? null,
    onSuccess: (billing) => uid && client.setQueryData(keys.billing(uid), billing),
  });
}

// ---------------------------------------------------------------- scenarios

export function useScenarios() {
  const uid = useUid();
  return useQuery({
    queryKey: keys.scenarios(uid ?? "-"),
    queryFn: async () =>
      (await fetchApi("/scenarios", { response: ListScenariosResponse })).scenarios,
    enabled: !!uid,
  });
}

export function useScenario(id: string | undefined) {
  const uid = useUid();
  return useQuery({
    queryKey: keys.scenario(id ?? "-"),
    queryFn: () => fetchApi(`/scenarios/${encodeURIComponent(id!)}`, { response: Scenario }),
    enabled: !!uid && !!id,
    refetchInterval: (query) => {
      // A scenario that failed to load (expired, 404) won't come back by asking again.
      if (query.state.status === "error") return false;
      const s = query.state.data;
      if (!s) return POLL_MS;
      return isJobActive(s.job) ? POLL_MS : false;
    },
  });
}

export function useCreateScenario() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateScenarioRequest) =>
      fetchApi("/scenarios", { body: req, request: CreateScenarioRequest, response: Scenario }),
    onSuccess: (scenario) => {
      client.setQueryData(keys.scenario(scenario.uuid), scenario);
      if (uid) void client.invalidateQueries({ queryKey: keys.scenarios(uid) });
    },
  });
}

/** PATCH /api/scenarios/:id: a saved what-if's new title. */
export function useRenameScenario() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      fetchApi(`/scenarios/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: RenameScenarioRequest.create({ title }),
        request: RenameScenarioRequest,
        response: Scenario,
      }),
    onSuccess: (renamed) => {
      // The answer has no plan: keep the cached one.
      client.setQueryData<Scenario>(keys.scenario(renamed.uuid), (s) =>
        s ? { ...s, title: renamed.title } : s,
      );
      if (uid) {
        client.setQueryData<Scenario[]>(keys.scenarios(uid), (list) =>
          list?.map((s) => (s.uuid === renamed.uuid ? { ...s, title: renamed.title } : s)),
        );
        void client.invalidateQueries({ queryKey: keys.scenarios(uid) });
      }
    },
  });
}

export function useDeleteScenario() {
  const uid = useUid();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/scenarios/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: (_res, id) => {
      client.removeQueries({ queryKey: keys.scenario(id) });
      if (uid) void client.invalidateQueries({ queryKey: keys.scenarios(uid) });
    },
  });
}
